/* ═══════════════════════════════════════════════════════════════
   The Designer — the forma's data life in Design mode (Sync appendix
   item 9: project formas, same shared document, same Publish gate).

   A project forma is mastered like the master: everyone in it edits ONE
   live document (`forma:pf-…`), kept on the server on every change — the
   SAME binding the Master Forma Studio uses (forma-studio/useSharedForma).
   The published columns every road out reads (project spec export, the
   Formatter, PDF, Export from Masters) change only on **Publish**
   (`formas.publish`). There is no autosave any more: the draft IS the
   shared document.

     · a PROJECT forma copy opens its own shared document straight away;
     · a MASTER resolved for a project spec is copy-on-write — the first
       edit forks the project's copy via FormaService.ensureProjectCopy
       (the same model the rest of the app uses), then the copy's shared
       document opens and the edit lands in it. The master is never
       touched from inside a project. (Josh law: masters are templates.)

   Undo/redo is the Yjs undo manager (this window's edits only). The
   document editor keeps its OWN history — the two are merged into one
   user-facing timeline by the undo coordinator (undoCoordinator.ts,
   contract §2.7), which is why `edit` reports every user action through
   `onAction` and undo/redo report whether they actually moved.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type * as Y from 'yjs';
import type { StandardConfig } from '../../shared/types';
import { FormaService } from '../services/formaService';
import { reportError } from '../services/errorLog';
import type { CollabPeer, LiveBlock } from '../spec-studio/collabState';
import { useSharedForma } from './useSharedForma';
import {
  FORMA_LOCKED_MESSAGE,
  FORMA_OPENING_MESSAGE,
  projectFormaTarget,
  queueReady,
  replayQueuedEdits,
  stripDerived,
  waitUntil,
  type FormaChipState,
} from './formaDraftModel';

/** How long Save / Back waits for the forked copy's document to take the
 *  queued edits before saying so. */
const FLUSH_TIMEOUT_MS = 15000;

export interface FormaDraftProject {
  id: string;
  number?: string;
  name?: string;
}

export interface FormaDraftOpts {
  /** The paper is live and editable (false = View: the forma is inert and
   *  this window is not announced in anyone's peer chips). Default true. */
  editing?: boolean;
  /** formas.edit — may write the shared draft. Default true. */
  canEdit?: boolean;
  /** formas.publish — may Publish / Discard draft. Default false. */
  canPublish?: boolean;
  /** The shared document ended under this window (4409 / 4410 / Discard). */
  onEnded?: (info: { reset: boolean; own: boolean }) => void;
}

export interface FormaDraft {
  /** The forma the Design-mode paper renders and the palettes edit. */
  forma: StandardConfig | null;
  /** The shared draft holds unpublished edits (Pending publish). */
  dirty: boolean;
  /** A fork or a Publish is in flight. */
  saving: boolean;
  /** True once the draft is (or has been forked into) a project copy. */
  isProjectCopy: boolean;
  /** Apply an edit. Pure transform — never mutate the input. */
  edit: (fn: (f: StandardConfig) => StandardConfig) => void;
  /** Wait for a fork in flight (mode switch, leaving the surface). The draft
   *  itself is kept by the server on every change — nothing else to write. */
  flush: () => Promise<void>;
  /** Undo one formatting step. False = there was nothing to undo. */
  undo: () => boolean;
  /** Redo one formatting step. False = there was nothing to redo. */
  redo: () => boolean;
  canUndo: boolean;
  canRedo: boolean;
  /** Write the shared draft to the published project forma (formas.publish). */
  publish: () => Promise<StandardConfig | undefined>;
  /** Throw the shared draft away for everyone: the published forma stays. */
  discardDraft: () => Promise<boolean>;
  /** Published / Pending publish / Saving… / Offline / Connecting…. */
  chip: FormaChipState;
  /** Other people in this forma's draft. */
  peers: CollabPeer[];
  canPublish: boolean;
  /** A shared document is open (false = a master not yet forked). */
  live: boolean;
  /** The open document (Review applies its accepted settings into it). */
  ydoc: Y.Doc | null;
  /** The forma as the document holds it right now (null until seeded). */
  docForma: StandardConfig | null;
  /** The row the shared document lives on (the project copy). */
  formaId: string | null;
  /** The PUBLISHED forma — what every road out reads. */
  published: StandardConfig | null;
  /** The socket is refused in a way no retry fixes. */
  blocked: LiveBlock | null;
  /** Edits wait for the forked copy's document (flush drains them). */
  queued: boolean;
}

export function useFormaDraft(
  base: StandardConfig | null,
  project: FormaDraftProject | null,
  userName: string,
  userId: string | undefined,
  onError: (msg: string) => void,
  /** Called once per USER edit (never for undo/redo) so the workspace's
   *  universal-undo timeline knows a formatting action just happened. */
  onAction?: () => void,
  opts?: FormaDraftOpts
): FormaDraft {
  const editing = opts?.editing !== false;
  const canEdit = opts?.canEdit !== false;
  const canPublish = opts?.canPublish === true;
  const optsRef = React.useRef(opts);
  optsRef.current = opts;
  const onErrorRef = React.useRef(onError);
  onErrorRef.current = onError;
  const onActionRef = React.useRef(onAction);
  onActionRef.current = onAction;
  const projectRef = React.useRef(project);
  projectRef.current = project;

  // The project copy this window forked (copy-on-write from a master).
  const [forked, setForked] = React.useState<StandardConfig | null>(null);
  // A different forma arriving from the resolver (not the copy we forked,
  // nor the master it came from) starts over.
  React.useEffect(() => {
    if (forked && base && base.id !== forked.id && base.id !== forked.sourceFormaId) {
      setForked(null);
    }
  }, [base, forked]);

  const { liveId, needsFork } = projectFormaTarget({ base, hasProject: !!project, forked });
  const target = forked ?? base;

  // The PUBLISHED row the document seeds from: the resolver's / the fork's,
  // refreshed by a re-read or our own Publish.
  const [published, setPublished] = React.useState<StandardConfig | null>(
    target ? stripDerived(target) : null
  );
  React.useEffect(() => {
    setPublished(target ? stripDerived(target) : null);
  }, [target]);

  const savedOnceRef = React.useRef(true);
  const liveIdRef = React.useRef(liveId);
  liveIdRef.current = liveId;

  const shared = useSharedForma({
    liveId,
    published,
    ready: !!published && published.id === liveId,
    userName,
    userId,
    editing,
    canEdit,
    canPublish,
    reloadPublished: async () => {
      const id = liveIdRef.current;
      if (!id) return;
      const row = await FormaService.getFresh(id);
      if (row) setPublished(stripDerived(row));
    },
    onPublishedRow: setPublished,
    onPublished: (saved) => setPublished(saved),
    onEnded: (info) => optsRef.current?.onEnded?.(info),
    savedOnceRef,
  });

  /* ─── Copy-on-write: edits made before the copy's document is open ─── */
  const pendingRef = React.useRef<Array<(f: StandardConfig) => StandardConfig>>([]);
  const [optimistic, setOptimistic] = React.useState<StandardConfig | null>(null);
  const forkingRef = React.useRef<Promise<void> | null>(null);
  const [forking, setForking] = React.useState(false);

  const startFork = React.useCallback((master: StandardConfig) => {
    const proj = projectRef.current;
    if (forkingRef.current || !proj) return;
    setForking(true);
    forkingRef.current = FormaService.ensureProjectCopy(stripDerived(master), proj)
      .then((copy) => setForked(copy))
      .catch((err: unknown) => {
        reportError('Designer:formaFork', err);
        pendingRef.current = [];
        setQueuedCount(0);
        setOptimistic(null);
        onErrorRef.current('Formatting change not saved — the project copy could not be made');
      })
      .finally(() => {
        forkingRef.current = null;
        setForking(false);
      });
  }, []);

  // The copy's document is open, synced and seeded: the queued edits land
  // in it, in order. A refusal (the forma is locked) keeps the queue — the
  // next change of the document, or a flush, tries again.
  const sharedEdit = shared.edit;
  const sharedEditRef = React.useRef(sharedEdit);
  sharedEditRef.current = sharedEdit;
  const [queuedCount, setQueuedCount] = React.useState(0);
  const lockedRef = React.useRef(false);
  const ready = queueReady({
    forkedId: forked?.id ?? null,
    liveId,
    synced: shared.synced,
    seeded: !!shared.docForma,
  });
  const readyRef = React.useRef(ready);
  readyRef.current = ready;
  /** True when nothing is left queued. */
  const replay = React.useCallback((): boolean => {
    if (!pendingRef.current.length) return true;
    if (!readyRef.current) return false;
    const r = replayQueuedEdits(pendingRef.current, (fn) => sharedEditRef.current(fn));
    pendingRef.current = r.remaining;
    setQueuedCount(r.remaining.length);
    if (r.blocked) {
      if (!lockedRef.current) onErrorRef.current(FORMA_LOCKED_MESSAGE);
      lockedRef.current = true;
      return false;
    }
    lockedRef.current = false;
    setOptimistic(null);
    return true;
  }, []);
  React.useEffect(() => {
    if (ready) replay();
  }, [ready, shared.docForma, replay]);

  const baseRef = React.useRef(base);
  baseRef.current = base;
  const needsForkRef = React.useRef(needsFork);
  needsForkRef.current = needsFork;

  const edit = React.useCallback(
    (fn: (f: StandardConfig) => StandardConfig) => {
      if (!editing || !canEdit) return;
      const master = baseRef.current;
      if ((needsForkRef.current || pendingRef.current.length) && master) {
        pendingRef.current.push(fn);
        setQueuedCount(pendingRef.current.length);
        setOptimistic((prev) => fn(prev ?? stripDerived(master)));
        onActionRef.current?.();
        if (needsForkRef.current) startFork(master);
        return;
      }
      if (sharedEdit(fn)) onActionRef.current?.();
    },
    [editing, canEdit, sharedEdit, startFork]
  );

  // Leaving / saving: the fork, then the copy's document opening, syncing
  // and taking the queued edits. Bounded — a forma that cannot take them
  // says why instead of losing them.
  const flush = React.useCallback(async () => {
    if (forkingRef.current) await forkingRef.current;
    if (!pendingRef.current.length) return;
    const drained = await waitUntil(() => replay(), FLUSH_TIMEOUT_MS);
    if (!drained) {
      throw new Error(lockedRef.current ? FORMA_LOCKED_MESSAGE : FORMA_OPENING_MESSAGE);
    }
  }, [replay]);

  const docForma = shared.docForma;
  const forma =
    optimistic ?? (editing && docForma ? docForma : (published ?? docForma ?? base ?? null));

  return {
    forma,
    dirty: shared.dirty || !!optimistic,
    saving: shared.saving || forking,
    isProjectCopy: !!(forked ?? forma)?.projectId,
    edit,
    flush,
    undo: shared.undo,
    redo: shared.redo,
    canUndo: shared.canUndo,
    canRedo: shared.canRedo,
    publish: shared.publish,
    discardDraft: shared.discardDraft,
    chip: optimistic || forking ? 'saving' : shared.chip,
    peers: shared.peers,
    canPublish,
    live: !!liveId,
    ydoc: liveId ? shared.ydoc : null,
    docForma,
    formaId: liveId,
    published,
    blocked: shared.blocked,
    queued: queuedCount > 0,
  };
}
